import {
  Button,
  Loading,
  Menu,
  MenuItem,
  MenuTrigger,
  notify,
  RowInput,
} from '@affine/component';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { WorkspaceDialogService } from '@affine/core/modules/dialogs';
import {
  type Member,
  MemberSearchService,
} from '@affine/core/modules/permissions';
import { UserFriendlyError } from '@affine/error';
import { DocRole, WorkspaceMemberStatus } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { track } from '@affine/track';
import { ArrowLeftBigIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import clsx from 'clsx';
import { debounce } from 'lodash-es';
import {
  type CompositionEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Virtuoso } from 'react-virtuoso';

import { Scroller } from '@affine/core/modules/share-menu/view/share-menu/scroller';
import * as styles from './invite-member-editor.css';
import { MemberItem } from './member-item';
import { SelectedMemberItem } from './selected-member-item';
import { TeamSearchService } from '@affine/core/modules/permissions/services/team-search';
import { OrganizeService } from '@affine/core/modules/organize';
import { FolderRole, type FolderPermissionDetails } from '@affine/core/modules/db/schema/schema';

const getRoleName = (role: FolderRole, t: ReturnType<typeof useI18n>) => {
  switch (role) {
    case FolderRole.Admin:
      return '可管理';
    case FolderRole.Write:
      return '可编辑';
    case FolderRole.Read:
      return '可查看';
    default:
      return '禁止访问';
  }
};

export type VMember = Member & {
  isTeam?: boolean;
}

export const InviteMemberEditor = ({
  onClickCancel,
  nodeId,
}: {
  onClickCancel: () => void;
  nodeId: string;
}) => {
  const t = useI18n();
  const [selectedMembers, setSelectedMembers] = useState<VMember[]>([]);
  const [inviteDocRoleType, setInviteDocRoleType] = useState<FolderRole>(
    FolderRole.Read
  );

  const memberSearchService = useService(MemberSearchService);
  const teamSearchService = useService(TeamSearchService);
  const organizeService = useService(OrganizeService);

  const node = useLiveData(
    organizeService.folderTree.folderNode$(nodeId)
  );

  useEffect(() => {
    // reset the search text when the component is mounted
    memberSearchService.reset();
    memberSearchService.loadMore();
  }, [memberSearchService]);

  const debouncedSearch = useMemo(
    () => debounce((value: string) => {
      memberSearchService.search(value)
      teamSearchService.search(value);
    }, 300),
    [memberSearchService]
  );

  const inputRef = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);
  const [composing, setComposing] = useState(false);
  const [searchText, setSearchText] = useState('');

  const handleValueChange = useCallback(
    (value: string) => {
      setSearchText(value);
      if (!composing) {
        debouncedSearch(value);
      }
    },
    [composing, debouncedSearch]
  );
  const workspaceDialogService = useService(WorkspaceDialogService);

  const onInvite = useAsyncCallback(async () => {


    const selectedMemberIds = selectedMembers.filter(member => !member.isTeam).map(member => member.id);
    const selectedTeamIds = selectedMembers.filter(member => member.isTeam).map(member => member.id);

    try {
      // await docGrantedUsersService.grantUsersRole(
      //   selectedMemberIds,
      //   selectedTeamIds,
      //   inviteDocRoleType
      // );

      const newPermission = selectedMemberIds.map(memberId => ({
        id: memberId,
        role: inviteDocRoleType,
        type: 'user'
      })).concat(
        selectedTeamIds.map(teamId => ({
          id: teamId,
          role: inviteDocRoleType,
          type: 'team',
        }))
      ) as FolderPermissionDetails[];

      node?.addPermission(newPermission);

      onClickCancel();

      notify.success({
        title: '添加成功',
      });
    } catch (error) {
      const err = UserFriendlyError.fromAny(error);
      notify.error({
        title: t[`error.${err.name}`](err.data),
      });
    }
  }, [
    inviteDocRoleType,
    onClickCancel,
    selectedMembers,
    t,
  ]);

  const handleCompositionStart: CompositionEventHandler<HTMLInputElement> =
    useCallback(() => {
      setComposing(true);
    }, []);

  const handleCompositionEnd: CompositionEventHandler<HTMLInputElement> =
    useCallback(
      e => {
        setComposing(false);
        debouncedSearch(e.currentTarget.value);
      },
      [debouncedSearch]
    );

  const focusInput = useCallback(() => {
    inputRef.current?.focus();
  }, []);
  const onFocus = useCallback(() => {
    setFocused(true);
  }, []);
  const onBlur = useCallback(() => {
    setFocused(false);
  }, []);

  const handleRemoved = useCallback(
    (memberId: string) => {
      setSelectedMembers(prev => prev.filter(member => member.id !== memberId));
      focusInput();
    },
    [focusInput]
  );

  const switchToMemberManagementTab = useCallback(() => {
    workspaceDialogService.open('setting', {
      activeTab: 'workspace:members',
    });
  }, [workspaceDialogService]);

  const handleClickMember = useCallback(
    (member: Member) => {
      setSelectedMembers(prev => {
        if (prev.some(m => m.id === member.id)) {
          // if the member is already in the list, just return
          return prev;
        }
        return [...prev, member];
      });
      setSearchText('');
      memberSearchService.search('');
      focusInput();
    },
    [focusInput, memberSearchService]
  );

  const handleRoleChange = useCallback((role: FolderRole) => {
    setInviteDocRoleType(role);
  }, []);

  return (
    <div className={styles.containerStyle}>
      <div className={styles.memberListStyle}>
        <div
          className={clsx(styles.InputContainer, {
            focus: focused,
          })}
        >
          <div className={styles.inlineMembersContainer}>
            {selectedMembers.map((member, idx) => {
              if (!member) {
                return null;
              }
              const onRemoved = () => handleRemoved(member.id);
              return (
                <SelectedMemberItem
                  key={member.id}
                  idx={idx}
                  onRemoved={onRemoved}
                  member={member}
                />
              );
            })}
            <RowInput
              ref={inputRef}
              value={searchText}
              onChange={handleValueChange}
              onCompositionStart={handleCompositionStart}
              onCompositionEnd={handleCompositionEnd}
              onFocus={onFocus}
              onBlur={onBlur}
              autoFocus
              className={styles.searchInput}
              placeholder={
                selectedMembers.length
                  ? ''
                  : t['com.affine.share-menu.invite-editor.placeholder']()
              }
            />
          </div>
          {!selectedMembers.length ? null : (
            <RoleSelector
              inviteDocRoleType={inviteDocRoleType}
              onRoleChange={handleRoleChange}
            />
          )}
        </div>
        <div className={styles.resultContainer}>
          <Result onClickMember={handleClickMember} />
        </div>
      </div>
      <div className={styles.footerStyle}>
        <span
          className={styles.manageMemberStyle}
        // onClick={switchToMemberManagementTab}
        >
          {/* {t['com.affine.share-menu.invite-editor.manage-members']()} */}
        </span>
        <div className={styles.buttonsContainer}>
          <Button className={styles.button} onClick={onClickCancel}>
            {t['Cancel']()}
          </Button>
          <Button
            className={styles.button}
            variant="primary"
            disabled={!selectedMembers.length}
            onClick={onInvite}
          >
            添加
          </Button>
        </div>
      </div>
    </div>
  );
};

const Result = ({
  onClickMember,
}: {
  onClickMember: (member: Member) => void;
}) => {
  const memberSearchService = useService(MemberSearchService);
  const teamSearchService = useService(TeamSearchService);
  const searchText = useLiveData(memberSearchService.searchText$);
  const result = useLiveData(memberSearchService.result$);
  const isLoading = useLiveData(memberSearchService.isLoading$);
  const teamResult = useLiveData(teamSearchService.result$);

  const activeMembers = useMemo(() => {

    const mergeResult: VMember[] = [];

    mergeResult.push(...result.filter(
      member => member.status === WorkspaceMemberStatus.Accepted
    ).map(member => ({
      ...member,
      isTeam: false,
    })));
    mergeResult.push(...teamResult.map(team => ({
      id: team.id,
      name: team.name,
      email: team.parent?.name || '',
      isTeam: true,
    }) as any));

    return mergeResult;
  }, [result, teamResult]);

  const itemContentRenderer = useCallback(
    (_index: number, data: Member) => {
      return <MemberItem member={data} onSelect={onClickMember} />;
    },
    [onClickMember]
  );

  const t = useI18n();

  const loadMore = useCallback(() => {
    memberSearchService.loadMore();
  }, [memberSearchService]);

  if (!searchText) {
    return null;
  }

  if (!activeMembers || activeMembers.length === 0) {
    if (isLoading) {
      return <Loading />;
    }
    return (
      <div className={styles.noFound}>
        {t['com.affine.share-menu.invite-editor.no-found']()}
      </div>
    );
  }

  return activeMembers.length < 8 ? (
    <div>
      {activeMembers.map(member => (
        <MemberItem key={member.id} member={member} onSelect={onClickMember} />
      ))}
    </div>
  ) : (
    <Virtuoso
      components={{
        Scroller,
      }}
      data={activeMembers}
      itemContent={itemContentRenderer}
      endReached={loadMore}
    />
  );
};

const RoleSelector = ({
  inviteDocRoleType,
  onRoleChange,
}: {
  inviteDocRoleType: FolderRole;
  onRoleChange: (role: FolderRole) => void;
}) => {
  const t = useI18n();
  const currentRoleName = useMemo(
    () => getRoleName(inviteDocRoleType, t),
    [inviteDocRoleType, t]
  );

  const changeToAdmin = useCallback(
    () => onRoleChange(FolderRole.Admin),
    [onRoleChange]
  );
  const changeToWrite = useCallback(() => {

    onRoleChange(FolderRole.Write);
  }, [onRoleChange]);
  const changeToRead = useCallback(() => {

    onRoleChange(FolderRole.Read);
  }, [onRoleChange]);
  return (
    <div className={styles.roleSelectorContainer}>
      <Menu
        contentOptions={{
          align: 'end',
        }}
        items={
          <>
            <MenuItem
              onSelect={changeToAdmin}
              selected={inviteDocRoleType === FolderRole.Admin}
            >
              {t['com.affine.share-menu.option.permission.can-manage']()}
            </MenuItem>
            <MenuItem
              onSelect={changeToWrite}
              selected={inviteDocRoleType === FolderRole.Write}
            >
              <div className={styles.planTagContainer}>
                {t['com.affine.share-menu.option.permission.can-edit']()}
              </div>
            </MenuItem>
            <MenuItem
              onSelect={changeToRead}
              selected={inviteDocRoleType === FolderRole.Read}
            >
              <div className={styles.planTagContainer}>
                {t['com.affine.share-menu.option.permission.can-read']()}
              </div>
            </MenuItem>
          </>
        }
      >
        <MenuTrigger
          className={styles.menuTriggerStyle}
          variant="plain"
          contentStyle={{
            width: '100%',
          }}
        >
          {currentRoleName}
        </MenuTrigger>
      </Menu>
    </div>
  );
};
